import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export const DOCS = [
  ["privacy_condomini", "Informativa privacy condomini"],
  ["accordo_contitolarita", "Accordo di contitolarità"],
  ["videosorveglianza", "Informativa videosorveglianza"],
  ["antincendio", "Informativa antincendio"],
  ["duvri", "DUVRI"],
];

const EMPTY = { nome: "", codice_fiscale: "", indirizzo: "", cap: "", citta: "", provincia: "", amministratore_nome: "", amministratore_piva: "", amministratore_email: "", amministratore_telefono: "", amministratore_indirizzo: "", amministratore_pec: "", documenti: ["privacy_condomini", "accordo_contitolarita"] };

const Field = ({ id, label, form, set, className = "", ...rest }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label htmlFor={id} className="text-xs text-slate-600">{label}</Label>
    <Input id={id} value={form[id]} onChange={(e) => set({ ...form, [id]: e.target.value })} data-testid={`condominio-${id.replace(/_/g, "-")}-input`} {...rest} />
  </div>
);

export const CondominioDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const toggle = (k) => setForm((f) => ({ ...f, documenti: f.documenti.includes(k) ? f.documenti.filter((d) => d !== k) : [...f.documenti, k] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { id, created_at, updated_at, ...payload } = form;
    try {
      if (initial?.id) await http.put(`/admin/condomini/${initial.id}`, payload);
      else await http.post("/admin/condomini", payload);
      toast.success(initial?.id ? "Condominio aggiornato" : "Condominio creato");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const p = { form, set: setForm };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="condominio-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? "Modifica condominio" : "Nuovo condominio"}</DialogTitle>
          <DialogDescription>I dati vengono inseriti automaticamente nei documenti.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-6">
            <Field id="nome" label="Nome condominio *" required className="sm:col-span-4" {...p} />
            <Field id="codice_fiscale" label="Codice fiscale *" required className="sm:col-span-2" {...p} />
            <Field id="indirizzo" label="Indirizzo" className="sm:col-span-6" {...p} />
            <Field id="cap" label="CAP" className="sm:col-span-1" {...p} />
            <Field id="citta" label="Città" className="sm:col-span-4" {...p} />
            <Field id="provincia" label="Prov." maxLength={2} className="sm:col-span-1" {...p} />
          </div>
          <div>
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Amministratore</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="amministratore_nome" label="Nome / Ragione sociale" {...p} />
              <Field id="amministratore_piva" label="C.F. / Partita IVA" {...p} />
              <Field id="amministratore_indirizzo" label="Indirizzo sede (per raccomandata A/R)" className="sm:col-span-2" {...p} />
              <Field id="amministratore_pec" label="PEC" {...p} />
              <Field id="amministratore_email" label="Email" type="email" {...p} />
              <Field id="amministratore_telefono" label="Telefono" {...p} />
            </div>
          </div>
          <div>
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Documenti pubblicati</p>
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {DOCS.map(([k, label]) => (
                <label key={k} className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm text-slate-700 hover:bg-slate-50">
                  {label}
                  <Switch checked={form.documenti.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`condominio-doc-switch-${k}`} />
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="condominio-cancel-button" className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Annulla</button>
            <button type="submit" disabled={saving} data-testid="condominio-save-button" className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
